/**
 * Create Project Page
 * Multi-step project creation for homeowners
 * Re-skinned with editorial theme to match homeowner design system
 */

import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Check, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { MultiImageUpload } from "@/components/ui/image-upload";
import { propertyTypeLabel } from "@/features/onboarding/options";
import {
	Btn,
	Eyebrow,
	FloatPanel,
	Icn,
	secondaryBtnStyle,
} from "@/features/projects/components/homeowner/design-primitives";
import { createProject } from "@/features/projects/services/projects-api.service";
import {
	getProperties,
	type Property,
} from "@/features/settings/services/settings-api.service";
import { StudioButton } from "@/features/studio/theme";
import { queryKeys } from "@/lib/query-keys";

const ROOMS = [
	"Living Room",
	"Master Bedroom",
	"Bedroom 2",
	"Bedroom 3",
	"Kitchen",
	"Dining Room",
	"Bathroom",
	"Study/Office",
	"Balcony",
	"Terrace",
];

const TIMELINE_OPTIONS = [
	{ value: "immediately", labelKey: "createProject.immediately" },
	{ value: "within_month", labelKey: "createProject.withinMonth" },
	{ value: "1_3_months", labelKey: "createProject.oneToThreeMonths" },
	{ value: "3_6_months", labelKey: "createProject.threeToSixMonths" },
	{ value: "flexible", labelKey: "createProject.flexible" },
];

type Step = "scope" | "details" | "budget" | "review";

const STEPS = [
	{ key: "scope", labelKey: "createProject.steps.scope" },
	{ key: "details", labelKey: "createProject.steps.details" },
	{ key: "budget", labelKey: "createProject.steps.budget" },
	{ key: "review", labelKey: "createProject.steps.review" },
] as const;

const ACCENT = "var(--color-dashboard-accent)";

function EditorialStepProgress({
	steps,
	currentStep,
}: {
	steps: readonly { key: string; labelKey: string }[];
	currentStep: Step;
}) {
	const currentIndex = steps.findIndex((s) => s.key === currentStep);

	return (
		<div style={{ marginBottom: 40 }}>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 12,
				}}
			>
				{steps.map((step, index) => {
					const isActive = index === currentIndex;
					const isComplete = index < currentIndex;
					return (
						<div
							key={step.key}
							style={{
								display: "flex",
								alignItems: "center",
								gap: 8,
							}}
						>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									width: 24,
									height: 24,
									borderRadius: "50%",
									background: isActive
										? ACCENT
										: isComplete
											? "var(--color-dashboard-pill-done-bg)"
											: "var(--color-dashboard-divider)",
									color: isActive
										? "var(--color-dashboard-on-fill)"
										: isComplete
											? "var(--color-dashboard-success)"
											: "var(--color-dashboard-muted)",
									fontSize: 11,
									fontWeight: 700,
									fontFamily: "var(--font-body)",
								}}
							>
								{isComplete ? <Check size={14} strokeWidth={2.5} /> : index + 1}
							</div>
							<span
								style={{
									fontFamily: "var(--font-mono)",
									fontSize: 9.5,
									fontWeight: 600,
									letterSpacing: "0.1em",
									textTransform: "uppercase" as const,
									color: isActive
										? "var(--color-dashboard-text)"
										: isComplete
											? "var(--color-dashboard-success)"
											: "var(--color-dashboard-muted)",
								}}
							>
								{step.labelKey
									.split(".")
									.pop()
									?.replace(/([A-Z])/g, " $1")
									.trim()}
							</span>
						</div>
					);
				})}
			</div>
			<div
				style={{
					display: "flex",
					gap: 4,
				}}
			>
				{steps.map((step, index) => (
					<div
						key={step.key}
						style={{
							flex: 1,
							height: 3,
							borderRadius: 69,
							background:
								index <= currentIndex
									? ACCENT
									: "var(--color-dashboard-divider)",
							transition: "background 400ms cubic-bezier(0.22,1,0.36,1)",
						}}
					/>
				))}
			</div>
		</div>
	);
}

function EditorialInput({
	label,
	placeholder,
	value,
	onChange,
	type = "text",
	onKeyDown,
}: {
	label?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
	type?: "text" | "number" | "email" | "password";
	onKeyDown?: React.KeyboardEventHandler<HTMLInputElement>;
}) {
	const [focused, setFocused] = useState(false);
	const inputId = `editorial-input-${label?.toLowerCase().replace(/\s+/g, "-") ?? Math.random().toString(36).slice(2, 8)}`;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{label && (
				<label
					htmlFor={inputId}
					style={{
						fontSize: 11,
						fontFamily: "var(--font-mono)",
						fontWeight: 600,
						letterSpacing: "0.1em",
						textTransform: "uppercase" as const,
						color: "var(--color-dashboard-muted)",
					}}
				>
					{label}
				</label>
			)}
			<input
				id={inputId}
				type={type}
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				onKeyDown={onKeyDown}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				style={{
					height: 44,
					paddingLeft: 14,
					paddingRight: 14,
					fontSize: 14,
					fontFamily: "var(--font-body)",
					color: "var(--color-dashboard-text)",
					background: "var(--color-dashboard-paper)",
					border: `1px solid ${focused ? ACCENT : "var(--color-dashboard-border)"}`,
					borderRadius: 7,
					outline: "none",
					transition: "border-color 200ms, box-shadow 200ms",
					boxShadow: focused
						? "0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)"
						: "none",
				}}
			/>
		</div>
	);
}

function EditorialTextarea({
	label,
	placeholder,
	value,
	onChange,
	rows = 4,
}: {
	label?: string;
	placeholder?: string;
	value: string;
	onChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
	rows?: number;
}) {
	const [focused, setFocused] = useState(false);
	const textareaId = `editorial-textarea-${Math.random().toString(36).slice(2, 8)}`;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{label && (
				<label
					htmlFor={textareaId}
					style={{
						fontSize: 11,
						fontFamily: "var(--font-mono)",
						fontWeight: 600,
						letterSpacing: "0.1em",
						textTransform: "uppercase" as const,
						color: "var(--color-dashboard-muted)",
					}}
				>
					{label}
				</label>
			)}
			<textarea
				id={textareaId}
				placeholder={placeholder}
				value={value}
				onChange={onChange}
				rows={rows}
				onFocus={() => setFocused(true)}
				onBlur={() => setFocused(false)}
				style={{
					padding: "12px 14px",
					fontSize: 14,
					fontFamily: "var(--font-body)",
					color: "var(--color-dashboard-text)",
					background: "var(--color-dashboard-paper)",
					border: `1px solid ${focused ? ACCENT : "var(--color-dashboard-border)"}`,
					borderRadius: 7,
					outline: "none",
					resize: "vertical" as const,
					minHeight: rows * 24 + 24,
					transition: "border-color 200ms, box-shadow 200ms",
					boxShadow: focused
						? "0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)"
						: "none",
				}}
			/>
		</div>
	);
}

function EditorialSelect({
	label,
	value,
	onChange,
	children,
}: {
	label?: string;
	value: string;
	onChange: (value: string) => void;
	children: React.ReactNode;
}) {
	const [focused, setFocused] = useState(false);
	const selectId = `editorial-select-${Math.random().toString(36).slice(2, 8)}`;

	return (
		<div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
			{label && (
				<label
					htmlFor={selectId}
					style={{
						fontSize: 11,
						fontFamily: "var(--font-mono)",
						fontWeight: 600,
						letterSpacing: "0.1em",
						textTransform: "uppercase" as const,
						color: "var(--color-dashboard-muted)",
					}}
				>
					{label}
				</label>
			)}
			<div style={{ position: "relative" }}>
				<select
					id={selectId}
					value={value}
					onChange={(e) => onChange(e.target.value)}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					style={{
						width: "100%",
						height: 44,
						paddingLeft: 14,
						paddingRight: 40,
						fontSize: 14,
						fontFamily: "var(--font-body)",
						color: "var(--color-dashboard-text)",
						background: "var(--color-dashboard-paper)",
						border: `1px solid ${focused ? ACCENT : "var(--color-dashboard-border)"}`,
						borderRadius: 7,
						outline: "none",
						cursor: "pointer",
						appearance: "none" as const,
						backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23475569' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
						backgroundRepeat: "no-repeat",
						backgroundPosition: "right 14px center",
						transition: "border-color 200ms, box-shadow 200ms",
						boxShadow: focused
							? "0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 12%, transparent)"
							: "none",
					}}
				>
					{children}
				</select>
			</div>
		</div>
	);
}

function EditorialToggle({
	active,
	onClick,
	children,
	style,
}: {
	active: boolean;
	onClick: () => void;
	children: React.ReactNode;
	style?: React.CSSProperties;
}) {
	const [hover, setHover] = useState(false);

	return (
		<button
			type="button"
			onClick={onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				display: "inline-flex",
				alignItems: "center",
				height: 32,
				paddingLeft: 14,
				paddingRight: 14,
				fontSize: 13,
				fontFamily: "var(--font-body)",
				fontWeight: 500,
				color: active
					? "var(--color-dashboard-on-fill)"
					: hover
						? "var(--color-dashboard-text)"
						: "var(--color-dashboard-muted-dark)",
				background: active
					? "var(--color-dashboard-accent)"
					: hover
						? "var(--color-dashboard-divider)"
						: "transparent",
				border: active
					? "transparent"
					: "1px solid var(--color-dashboard-border)",
				borderRadius: 11,
				cursor: "pointer",
				transition: "all 200ms cubic-bezier(0.22,1,0.36,1)",
				...style,
			}}
		>
			{children}
		</button>
	);
}

function EditorialSwitch({
	checked,
	onChange,
}: {
	checked: boolean;
	onChange: (checked: boolean) => void;
}) {
	const [hover, setHover] = useState(false);

	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			onClick={() => onChange(!checked)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				position: "relative",
				width: 44,
				height: 24,
				padding: 0,
				background: checked
					? "var(--color-dashboard-accent)"
					: hover
						? "var(--color-dashboard-border)"
						: "var(--color-dashboard-warm-300)",
				border: "none",
				borderRadius: 12,
				cursor: "pointer",
				transition: "background 200ms",
				boxShadow: checked
					? "inset 0 1px 0 rgba(255,237,213,0.35), 0 2px 4px color-mix(in srgb, var(--color-dashboard-shadow-ink) 15%, transparent)"
					: "inset 0 1px 0 color-mix(in srgb, var(--color-dashboard-paper) 50%, transparent)",
			}}
		>
			<div
				style={{
					position: "absolute",
					top: 2,
					left: checked ? 22 : 2,
					width: 20,
					height: 20,
					background: "var(--color-dashboard-paper)",
					borderRadius: "50%",
					boxShadow:
						"0 1px 3px color-mix(in srgb, var(--color-dashboard-shadow-ink) 25%, transparent)",
					transition: "left 200ms cubic-bezier(0.22,1,0.36,1)",
				}}
			/>
		</button>
	);
}

function ScopeOptionCard({
	active,
	onClick,
	title,
	description,
}: {
	active: boolean;
	onClick: () => void;
	title: string;
	description: string;
}) {
	const [hover, setHover] = useState(false);

	return (
		<button
			type="button"
			onClick={onClick}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			style={{
				width: "100%",
				padding: "20px 22px",
				textAlign: "left",
				background: active
					? "linear-gradient(180deg, color-mix(in srgb, var(--color-dashboard-paper) 50%, transparent) 0%, color-mix(in srgb, var(--color-dashboard-paper) 0%, transparent) 50%), var(--color-dashboard-paper)"
					: hover
						? "var(--color-dashboard-surface)"
						: "var(--color-dashboard-paper)",
				border: `2px solid ${active ? ACCENT : hover ? "var(--color-border-default)" : "var(--color-dashboard-border)"}`,
				borderRadius: 10,
				cursor: "pointer",
				transition: "all 200ms cubic-bezier(0.22,1,0.36,1)",
				boxShadow: active
					? "0 0 0 3px color-mix(in srgb, var(--color-dashboard-accent) 8%, transparent), 0 4px 12px color-mix(in srgb, var(--color-dashboard-shadow-ink) 8%, transparent)"
					: hover
						? "0 2px 8px color-mix(in srgb, var(--color-dashboard-shadow-ink) 6%, transparent)"
						: "none",
			}}
		>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: 4,
				}}
			>
				<h3
					style={{
						fontFamily: "var(--font-display)",
						fontSize: 18,
						fontWeight: 500,
						color: "var(--color-dashboard-text)",
						margin: 0,
					}}
				>
					{title}
				</h3>
				{active && (
					<div
						style={{
							width: 20,
							height: 20,
							borderRadius: "50%",
							background: ACCENT,
							display: "flex",
							alignItems: "center",
							justifyContent: "center",
						}}
					>
						<Check
							size={12}
							className="text-dashboard-on-fill"
							strokeWidth={2.5}
						/>
					</div>
				)}
			</div>
			<p
				style={{
					fontFamily: "var(--font-body)",
					fontSize: 13,
					color: "var(--color-dashboard-muted)",
					margin: 0,
					lineHeight: 1.5,
				}}
			>
				{description}
			</p>
		</button>
	);
}

export function CreateProjectPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const [step, setStep] = useState<Step>("scope");
	const [isLoading, setIsLoading] = useState(false);
	const [properties, setProperties] = useState<Property[]>([]);

	const [title, setTitle] = useState("");
	const [scope, setScope] = useState<"full_home" | "partial">("full_home");
	const [selectedRooms, setSelectedRooms] = useState<string[]>([]);
	const [notes, setNotes] = useState("");
	const [propertyId, setPropertyId] = useState("");
	const [budgetMin, setBudgetMin] = useState("");
	const [budgetMax, setBudgetMax] = useState("");
	const [timelineWeeks, setTimelineWeeks] = useState("");
	const [startTimeline, setStartTimeline] = useState("within_month");
	const [beforeImages, setBeforeImages] = useState<string[]>([]);
	const [isPublic, setIsPublic] = useState(false);

	useEffect(() => {
		const loadProperties = async () => {
			const props = await getProperties();
			setProperties(props);
			if (props.length > 0) {
				setPropertyId(props[0].id);
			}
		};
		loadProperties();
	}, []);

	const toggleRoom = (room: string) => {
		setSelectedRooms((prev) =>
			prev.includes(room) ? prev.filter((r) => r !== room) : [...prev, room],
		);
	};

	const handleScopeNext = () => {
		if (scope === "partial" && selectedRooms.length === 0) {
			toast.error(t("createProject.selectAtLeastOneRoom"));
			return;
		}
		setStep("details");
	};

	const handleDetailsNext = () => {
		if (!title.trim()) {
			toast.error(t("createProject.enterProjectTitle"));
			return;
		}
		setStep("budget");
	};

	const handleBudgetNext = () => {
		if (!budgetMin || !budgetMax) {
			toast.error(t("createProject.enterBudgetRange"));
			return;
		}
		if (parseInt(budgetMin, 10) > parseInt(budgetMax, 10)) {
			toast.error(t("createProject.minCannotExceedMax"));
			return;
		}
		setStep("review");
	};

	const handleSubmit = async () => {
		setIsLoading(true);
		try {
			const result = await createProject({
				title: title.trim(),
				scope,
				propertyId: propertyId || undefined,
				scopeDetails:
					scope === "partial"
						? {
								rooms: selectedRooms,
								notes: notes.trim() || undefined,
							}
						: notes.trim()
							? { notes: notes.trim() }
							: undefined,
				budgetMin: parseInt(budgetMin, 10),
				budgetMax: parseInt(budgetMax, 10),
				timelineWeeks: timelineWeeks ? parseInt(timelineWeeks, 10) : undefined,
				startTimeline,
				beforeImages,
				isPublic,
			});

			if (result.success) {
				// Marks every cached projects list stale so the list refetches
				// the next time it is shown, and includes the new project.
				void queryClient.invalidateQueries({
					queryKey: queryKeys.projects.lists(),
				});
				toast.success(t("createProject.projectCreated"));
				navigate(`/projects/${result.project?.id}`);
			} else {
				toast.error(result.error || t("createProject.failedToCreateProject"));
			}
		} catch {
			toast.error(t("common.somethingWentWrong"));
		} finally {
			setIsLoading(false);
		}
	};

	const formatBudget = (value: string) => {
		const num = parseInt(value, 10);
		if (Number.isNaN(num)) return "";
		if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)} Cr`;
		if (num >= 100000) return `₹${(num / 100000).toFixed(0)} Lakh`;
		return `₹${num.toLocaleString("en-IN")}`;
	};

	return (
		<div
			style={{
				minHeight: "100vh",
				background:
					"linear-gradient(180deg, var(--color-dashboard-paper) 0%, var(--color-dashboard-divider) 100%)",
				paddingTop: 48,
				paddingBottom: 60,
			}}
		>
			<div style={{ maxWidth: 680, margin: "0 auto", padding: "0 24px" }}>
				<button
					type="button"
					onClick={() => navigate("/projects")}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: 6,
						background: "none",
						border: "none",
						padding: 0,
						cursor: "pointer",
						fontFamily: "var(--font-mono)",
						fontSize: 10,
						fontWeight: 600,
						letterSpacing: "0.1em",
						textTransform: "uppercase" as const,
						color: "var(--color-dashboard-muted)",
						marginBottom: 24,
						transition: "color 200ms",
					}}
					onMouseEnter={(e) =>
						(e.currentTarget.style.color = "var(--color-dashboard-text)")
					}
					onMouseLeave={(e) =>
						(e.currentTarget.style.color = "var(--color-dashboard-muted)")
					}
				>
					<ArrowLeft size={14} />
					Cancel
				</button>

				<EditorialStepProgress steps={STEPS} currentStep={step} />

				{step === "scope" && (
					<FloatPanel padding={32}>
						<Eyebrow style={{ marginBottom: 8 }}>
							{t("createProject.steps.scope")}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 32,
								lineHeight: 1.1,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "0 0 8px",
							}}
						>
							{t("createProject.whatDoYouWantToDesign")}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								margin: "0 0 28px",
							}}
						>
							{t("createProject.tellUsScope")}
						</p>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 12,
								marginBottom: 24,
							}}
						>
							<ScopeOptionCard
								active={scope === "full_home"}
								onClick={() => setScope("full_home")}
								title={t("createProject.fullHomeInterior")}
								description={t("createProject.fullHomeDesc")}
							/>
							<ScopeOptionCard
								active={scope === "partial"}
								onClick={() => setScope("partial")}
								title={t("createProject.specificRooms")}
								description={t("createProject.specificRoomsDesc")}
							/>
						</div>

						{scope === "partial" && (
							<div style={{ marginBottom: 24 }}>
								<p
									style={{
										fontSize: 11,
										fontFamily: "var(--font-mono)",
										fontWeight: 600,
										letterSpacing: "0.1em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										marginBottom: 12,
									}}
								>
									{t("createProject.selectRoomsToDesign")}
								</p>
								<div
									style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}
								>
									{ROOMS.map((room) => (
										<EditorialToggle
											key={room}
											active={selectedRooms.includes(room)}
											onClick={() => toggleRoom(room)}
										>
											{room}
										</EditorialToggle>
									))}
								</div>
							</div>
						)}

						<Btn
							variant="primary"
							size="lg"
							onClick={handleScopeNext}
							style={{ width: "100%" }}
						>
							{t("common.continue")}
							<Icn
								name="arrowRight"
								size={16}
								color="var(--color-dashboard-on-fill)"
							/>
						</Btn>
					</FloatPanel>
				)}

				{step === "details" && (
					<FloatPanel padding={32}>
						<Eyebrow style={{ marginBottom: 8 }}>
							{t("createProject.steps.details")}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 32,
								lineHeight: 1.1,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "0 0 8px",
							}}
						>
							{t("createProject.projectDetails")}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								margin: "0 0 28px",
							}}
						>
							{t("createProject.projectDetailsSubtitle")}
						</p>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 20,
								marginBottom: 28,
							}}
						>
							<EditorialInput
								label={t("createProject.projectTitle")}
								placeholder={t("createProject.projectTitlePlaceholder")}
								value={title}
								onChange={(e) => setTitle(e.target.value)}
							/>

							{properties.length > 0 && (
								<EditorialSelect
									label={t("createProject.property")}
									value={propertyId}
									onChange={setPropertyId}
								>
									{properties.map((prop) => {
										const propertyType = propertyTypeLabel(
											t,
											prop.type,
											prop.typeOther,
										);
										const cityText = prop.city || t("createProject.noCity");
										return (
											<option key={prop.id} value={prop.id}>
												{propertyType} - {cityText} (
												{prop.sizeSqft || t("createProject.notAvailable")}{" "}
												sq.ft)
											</option>
										);
									})}
								</EditorialSelect>
							)}

							<EditorialTextarea
								label={t("createProject.additionalNotes")}
								placeholder={t("createProject.notesPlaceholder")}
								value={notes}
								onChange={(e) => setNotes(e.target.value)}
								rows={4}
							/>

							<div>
								<p
									style={{
										fontSize: 11,
										fontFamily: "var(--font-mono)",
										fontWeight: 600,
										letterSpacing: "0.1em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										marginBottom: 10,
									}}
								>
									Before Images
								</p>
								<MultiImageUpload
									label="Before images"
									currentUrls={beforeImages}
									maxImages={24}
									onUpload={(urls) =>
										setBeforeImages((prev) => [...prev, ...urls])
									}
									onRemove={(index) =>
										setBeforeImages((prev) =>
											prev.filter((_, i) => i !== index),
										)
									}
								/>
							</div>

							<div
								style={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									padding: "16px 18px",
									background: "var(--color-dashboard-paper)",
									border: "1px solid var(--color-dashboard-border)",
									borderRadius: 8,
								}}
							>
								<div>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 14,
											fontWeight: 600,
											color: "var(--color-dashboard-text)",
											margin: 0,
										}}
									>
										Make project public
									</p>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 12,
											color: "var(--color-dashboard-muted)",
											margin: "2px 0 0",
										}}
									>
										Project will appear in public feed once work is in progress.
									</p>
								</div>
								<EditorialSwitch checked={isPublic} onChange={setIsPublic} />
							</div>
						</div>

						<div style={{ display: "flex", gap: 12 }}>
							<StudioButton
								variant="secondary"
								size="lg"
								onClick={() => setStep("scope")}
								style={{ ...secondaryBtnStyle("lg"), flex: 1 }}
							>
								<ArrowLeft size={16} />
								{t("common.back")}
							</StudioButton>
							<Btn
								variant="primary"
								size="lg"
								onClick={handleDetailsNext}
								style={{ flex: 2 }}
							>
								{t("common.continue")}
								<Icn
									name="arrowRight"
									size={16}
									color="var(--color-dashboard-on-fill)"
								/>
							</Btn>
						</div>
					</FloatPanel>
				)}

				{step === "budget" && (
					<FloatPanel padding={32}>
						<Eyebrow style={{ marginBottom: 8 }}>
							{t("createProject.steps.budget")}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 32,
								lineHeight: 1.1,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "0 0 8px",
							}}
						>
							{t("createProject.budgetTimeline")}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								margin: "0 0 28px",
							}}
						>
							{t("createProject.budgetTimelineSubtitle")}
						</p>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 24,
								marginBottom: 28,
							}}
						>
							<div>
								<p
									style={{
										fontSize: 11,
										fontFamily: "var(--font-mono)",
										fontWeight: 600,
										letterSpacing: "0.1em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										marginBottom: 10,
									}}
								>
									{t("createProject.budgetRangeRs")}
								</p>
								<div
									style={{
										display: "grid",
										gridTemplateColumns: "1fr 1fr",
										gap: 12,
									}}
								>
									<div>
										<EditorialInput
											type="number"
											placeholder={t("createProject.minimum")}
											value={budgetMin}
											onChange={(e) => setBudgetMin(e.target.value)}
										/>
										{budgetMin && (
											<p
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 10,
													color: "var(--color-dashboard-muted)",
													marginTop: 4,
												}}
											>
												{formatBudget(budgetMin)}
											</p>
										)}
									</div>
									<div>
										<EditorialInput
											type="number"
											placeholder={t("createProject.maximum")}
											value={budgetMax}
											onChange={(e) => setBudgetMax(e.target.value)}
										/>
										{budgetMax && (
											<p
												style={{
													fontFamily: "var(--font-mono)",
													fontSize: 10,
													color: "var(--color-dashboard-muted)",
													marginTop: 4,
												}}
											>
												{formatBudget(budgetMax)}
											</p>
										)}
									</div>
								</div>
							</div>

							<div>
								<p
									style={{
										fontSize: 11,
										fontFamily: "var(--font-mono)",
										fontWeight: 600,
										letterSpacing: "0.1em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										marginBottom: 10,
									}}
								>
									{t("createProject.whenToStart")}
								</p>
								<div
									style={{ display: "flex", flexWrap: "wrap" as const, gap: 8 }}
								>
									{TIMELINE_OPTIONS.map((option) => (
										<EditorialToggle
											key={option.value}
											active={startTimeline === option.value}
											onClick={() => setStartTimeline(option.value)}
										>
											{t(option.labelKey)}
										</EditorialToggle>
									))}
								</div>
							</div>

							<EditorialInput
								label={t("createProject.expectedDuration")}
								placeholder="e.g., 12"
								value={timelineWeeks}
								onChange={(e) => setTimelineWeeks(e.target.value)}
								type="number"
							/>
						</div>

						<div style={{ display: "flex", gap: 12 }}>
							<StudioButton
								variant="secondary"
								size="lg"
								onClick={() => setStep("details")}
								style={{ ...secondaryBtnStyle("lg"), flex: 1 }}
							>
								<ArrowLeft size={16} />
								{t("common.back")}
							</StudioButton>
							<Btn
								variant="primary"
								size="lg"
								onClick={handleBudgetNext}
								style={{ flex: 2 }}
							>
								{t("createProject.review")}
								<Icn
									name="arrowRight"
									size={16}
									color="var(--color-dashboard-on-fill)"
								/>
							</Btn>
						</div>
					</FloatPanel>
				)}

				{step === "review" && (
					<FloatPanel padding={32}>
						<Eyebrow style={{ marginBottom: 8 }}>
							{t("createProject.steps.review")}
						</Eyebrow>
						<h2
							style={{
								fontFamily: "var(--font-display)",
								fontWeight: 500,
								fontSize: 32,
								lineHeight: 1.1,
								letterSpacing: "-0.02em",
								color: "var(--color-dashboard-text)",
								margin: "0 0 8px",
							}}
						>
							{t("createProject.reviewYourProject")}
						</h2>
						<p
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 14,
								color: "var(--color-dashboard-muted)",
								margin: "0 0 24px",
							}}
						>
							{t("createProject.reviewSubtitle")}
						</p>

						<div
							style={{
								display: "flex",
								flexDirection: "column",
								gap: 16,
								paddingBottom: 20,
								borderBottom: "1px solid var(--color-dashboard-border)",
								marginBottom: 20,
							}}
						>
							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									{t("createProject.projectTitle")}
								</p>
								<p
									style={{
										fontFamily: "var(--font-display)",
										fontSize: 20,
										fontWeight: 500,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{title}
								</p>
							</div>

							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									{t("project.scope")}
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{scope === "full_home"
										? t("createProject.fullHomeInterior")
										: t("createProject.specificRoomsLabel", {
												rooms: selectedRooms.join(", "),
											})}
								</p>
							</div>

							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									{t("project.budget")}
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{formatBudget(budgetMin)} - {formatBudget(budgetMax)}
								</p>
							</div>

							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									{t("project.timeline")}
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{t("createProject.startLabel", {
										timeline: t(
											TIMELINE_OPTIONS.find(
												(opt) => opt.value === startTimeline,
											)?.labelKey || "",
										),
									})}
									{timelineWeeks &&
										` — ${t("createProject.durationLabel", { weeks: timelineWeeks })}`}
								</p>
							</div>

							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									Before Images
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{beforeImages.length} uploaded
								</p>
							</div>

							<div>
								<p
									style={{
										fontFamily: "var(--font-mono)",
										fontSize: 9.5,
										letterSpacing: "0.12em",
										textTransform: "uppercase" as const,
										color: "var(--color-dashboard-muted)",
										margin: "0 0 4px",
									}}
								>
									Visibility
								</p>
								<p
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 14,
										color: "var(--color-dashboard-text)",
										margin: 0,
									}}
								>
									{isPublic ? "Public when project starts" : "Private project"}
								</p>
							</div>

							{notes && (
								<div>
									<p
										style={{
											fontFamily: "var(--font-mono)",
											fontSize: 9.5,
											letterSpacing: "0.12em",
											textTransform: "uppercase" as const,
											color: "var(--color-dashboard-muted)",
											margin: "0 0 4px",
										}}
									>
										{t("project.notes")}
									</p>
									<p
										style={{
											fontFamily: "var(--font-body)",
											fontSize: 14,
											color: "var(--color-dashboard-muted)",
											margin: 0,
											fontStyle: "italic",
										}}
									>
										{notes}
									</p>
								</div>
							)}
						</div>

						<div style={{ display: "flex", gap: 12 }}>
							<StudioButton
								variant="secondary"
								size="lg"
								onClick={() => setStep("budget")}
								style={{ ...secondaryBtnStyle("lg"), flex: 1 }}
							>
								<ArrowLeft size={16} />
								{t("common.back")}
							</StudioButton>
							<Btn
								variant="primary"
								size="lg"
								onClick={handleSubmit}
								style={{ flex: 2 }}
								icon={
									isLoading ? (
										<Loader2
											size={16}
											style={{ animation: "spin 1s linear infinite" }}
										/>
									) : (
										<Check size={16} className="text-dashboard-on-fill" />
									)
								}
							>
								{isLoading
									? t("common.creating")
									: t("createProject.createProjectBtn")}
							</Btn>
						</div>
					</FloatPanel>
				)}
			</div>
		</div>
	);
}
